import { Menu } from 'lucide-react';
import { Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { ThemeToggle } from '@/components/common/theme-toggle';
import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { useLocationReporter } from '@/features/location/use-location-reporter';
import { useBackgroundReporter } from '@/features/location/use-background-reporter';
import { useDeployReload } from '@/features/app/use-deploy-reload';
import { useAndroidBackButton } from '@/features/app/use-back-button';
import { Breadcrumbs } from './breadcrumbs';
import { CommandPalette, CommandPaletteTrigger, useCommandPalette } from './command-palette';
import { SidebarNav } from './sidebar-nav';
import { ChatAlerts } from '@/features/chat/chat-alerts';
import { usePushRegistration } from '@/features/chat/use-push-registration';
import { NotificationBell } from './notification-bell';
import { UserMenu } from './user-menu';

/**
 * Application chrome: fixed sidebar on desktop, drawer on mobile, sticky topbar.
 */
export function AppLayout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const palette = useCommandPalette();
  const { pathname } = useLocation();

  // Mounted once, for the whole signed-in app rather than one screen: a person
  // is not going to sit on a "location" page all day, and reporting only while
  // one screen is open would collect almost nothing. It stays silent unless the
  // server says reporting is on for this person.
  useLocationReporter();
  // Does nothing in a browser. In the Android shell it reports with the screen
  // off, which is the case the one above cannot cover.
  useBackgroundReporter();
  // The foreground service keeps this WebView alive for a whole shift, so a
  // deploy is picked up on returning to the app rather than on next launch.
  useDeployReload();
  // Android's hardware back button. Without this Capacitor's default closes the
  // app from wherever you happen to be standing. At the dashboard it asks
  // first, because closing the app also stops location reporting.
  const back = useAndroidBackButton();
  // Hands this handset's address to the server so a message can reach it once
  // the app is closed, which is the one case the poll cannot cover. Mounted
  // here rather than gated on a flag: this layout only renders behind
  // RequireAuth, so reaching it *is* being signed in. Does nothing in a
  // browser or in a shell built before push.
  usePushRegistration();

  // Route changes from anywhere — a breadcrumb, a redirect, the browser Back
  // button — must close the drawer, not just clicks on its own links.
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  return (
    // Padded by the phone's status and navigation bars, which the Android
    // shell draws the page underneath. `--inset-top/bottom` come from the
    // safe-area environment and, inside the shell, never below a floor.
    <div className="flex h-dvh overflow-hidden bg-background pb-[var(--inset-bottom)] pt-[var(--inset-top)]">
      {/* Skip link: the first Tab stop on the page, so a keyboard user is not
          forced through thirty nav items to reach the content. */}
      <a
        href="#main-content"
        className="sr-only z-50 focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:rounded-md focus:bg-primary focus:px-4 focus:py-2 focus:text-primary-foreground"
      >
        Skip to content
      </a>

      <aside className="hidden w-64 shrink-0 border-r border-sidebar-border lg:block">
        <SidebarNav />
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-3 border-b bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="lg:hidden" aria-label="Open navigation">
                <Menu />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 p-0 pt-[var(--inset-top)]">
              {/* Radix requires a title for the dialog's accessible name; it is
                  visually hidden because the drawer shows the brand already. */}
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <SidebarNav onNavigate={() => setDrawerOpen(false)} />
            </SheetContent>
          </Sheet>

          <Breadcrumbs />

          <div className="ml-auto flex items-center gap-1">
            {/* First in the group, and the only wide control: it is the way to
                anywhere, and the icons beside it are single destinations. */}
            <CommandPaletteTrigger onOpen={() => palette.setOpen(true)} />
            {/* Before the theme toggle: this one has a number on it. */}
            <ChatAlerts />
            <NotificationBell />
            <ThemeToggle />
            <UserMenu />
          </div>
        </header>

        <CommandPalette open={palette.open} onOpenChange={palette.setOpen} />

        <main id="main-content" className="flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[1600px] space-y-6 p-4 sm:p-6">
            {/* Covers every lazily-loaded page. Placed here rather than around
                the router so an arriving chunk swaps only the content area —
                a boundary above the chrome would blank the sidebar and topbar
                on each first visit to a section. */}
            <Suspense fallback={<PageFallback />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>

      {/*
        Only ever opened by the hardware back button, so it costs a browser
        nothing — the hook never sets it outside the Android shell.
      */}
      <ConfirmDialog
        open={back.exitPromptOpen}
        onOpenChange={back.setExitPromptOpen}
        title="Close KioBytes Telecom?"
        description="Location reporting stops while the app is closed, and starts again when you open it. Your shift and your punches are not affected."
        confirmLabel="Close the app"
        cancelLabel="Stay"
        onConfirm={back.confirmExit}
      />
    </div>
  );
}

/**
 * Shown while a page's chunk is still on the wire.
 *
 * Shaped like the page it is standing in for — a heading, then a body — rather
 * than a spinner in the middle of the screen: the layout then does not jump
 * when the real thing arrives, and on a fast connection this is a flicker
 * nobody registers.
 */
function PageFallback() {
  return (
    <div className="space-y-6" aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading</span>
      <div className="space-y-2">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-4 w-72" />
      </div>
      <Skeleton className="h-64 w-full rounded-lg" />
    </div>
  );
}
